import { LegacyOrganizationAccount } from "../api/types/Account";
import { TLegacyUserWithAccount } from "../api/types/User";
import { TLegacyUserRole } from "../api/types/UserRole";
import {
  ACCOUNT_MAP_ICON_SIZE_DEFAULT_VALUE,
  ACCOUNT_MAP_ICON_SIZE_VALUES,
  APP_CONFIG,
} from "../constants/constants";
import { ACL_NAMES, useACL } from "../hooks/acl";
import { routeUrls } from "../navigation/routeUrls";
import {
  DISTANCE_UNITS_OPTIONS,
  ECONOMY_UNITS_OPTIONS,
  SPEED_UNITS_OPTIONS,
  VOLUME_UNITS_OPTIONS,
} from "../pages/admin-details/account/constants";
import { ROLE_ACCESS_LEVELS } from "../pages/admin-details/role/constants";
import { localStorageKeys, useLocalStorage } from "./localStorageItems";

type TUseLoggedInUserData = (
  type:
    | "toggleFT"
    | "ownerOrganizationId"
    | "distanceUnits"
    | "speedUnits"
    | "volumeUnits"
    | "economyUnits"
    | "clusteringEnabled"
    | "timezone" // deprecated, we now use redux store
    | "labels"
    | "groupHierarchy"
    | "viewVideoAllowed"
    | "cameraRecorderType"
    | "cameraRecorderConfigAllowed"
    | "hasdrivers"
    | "hasfleettags"
    | "haspanicsos"
    | "elogenabled"
    | "isbordercrossing"
    | "crashtrackEnabled"
    | "geoCategoriesEnabled"
    | "mapDefault"
    | "assetimageonmap"
    | "mapIconSize"
    | "incidentreviewallowed"
    | "incabcoachingallowed"
    | "serializedziptieallowed"
    | "userloginasallowed"
) => string | null;

export const useLoggedInUserData: TUseLoggedInUserData = (type) => {
  const { getLocalStorageItem } = useLocalStorage();
  const thisUser = {
    ownerOrganization: getLocalStorageItem(
      localStorageKeys.ownerOrganization
    ) as LegacyOrganizationAccount,
    user: getLocalStorageItem(localStorageKeys.user) as TLegacyUserWithAccount,
  };
  switch (type) {
    case "toggleFT":
      return ((thisUser.ownerOrganization || {}) as LegacyOrganizationAccount)
        ?.toggleft === 1
        ? APP_CONFIG.TOGGLE_FT_ALLOWED.YES
        : APP_CONFIG.TOGGLE_FT_ALLOWED.NO;

    case "ownerOrganizationId":
      return !!thisUser.ownerOrganization
        ? ((thisUser.ownerOrganization || {}) as LegacyOrganizationAccount)
            ?.accountid
        : ((thisUser.user || {}) as TLegacyUserWithAccount)?.account
            ?.accountid || null;

    case "clusteringEnabled":
      return (
        `${((thisUser.ownerOrganization || {}) as LegacyOrganizationAccount)?.enableclustering}` ||
        "0"
      );

    case "distanceUnits":
      return (
        `${((thisUser.ownerOrganization || {}) as LegacyOrganizationAccount)?.distanceunits}` ||
        DISTANCE_UNITS_OPTIONS[0].value
      );

    case "speedUnits":
      return (
        `${((thisUser.ownerOrganization || {}) as LegacyOrganizationAccount)?.speedunits}` ||
        SPEED_UNITS_OPTIONS[0].value
      );

    case "volumeUnits":
      return (
        `${((thisUser.ownerOrganization || {}) as LegacyOrganizationAccount)?.volumeunits}` ||
        VOLUME_UNITS_OPTIONS[0].value
      );

    case "economyUnits":
      return (
        `${((thisUser.ownerOrganization || {}) as LegacyOrganizationAccount)?.economyunits}` ||
        ECONOMY_UNITS_OPTIONS[0].value
      );

    case "timezone": // deprecated, we now use redux store
      return (
        ((thisUser.user || {}) as TLegacyUserWithAccount)?.account?.timezone ||
        null
      );

    case "labels":
      return JSON.stringify(
        (thisUser.ownerOrganization || {}) as LegacyOrganizationAccount
      );

    case "groupHierarchy":
      return (
        (
          (thisUser.user || {}) as TLegacyUserWithAccount
        )?.account?.usehierarchygroups?.toString() || "0"
      );

    case "viewVideoAllowed":
      return (
        (thisUser.ownerOrganization || {})?.viewvideoallowed?.toString() || "0"
      );

    case "cameraRecorderType":
      return (
        (thisUser.ownerOrganization || {})?.recordertype?.toString() || "0"
      );

    case "cameraRecorderConfigAllowed":
      return (
        (thisUser.ownerOrganization || {})?.recorderconfigallowed?.toString() ||
        "0"
      );

    case "hasdrivers":
      return (thisUser.ownerOrganization || {})?.hasdrivers?.toString() || "0";

    case "assetimageonmap":
      return (
        (thisUser.ownerOrganization || {})?.assetimageonmap?.toString() || "0"
      );

    case "incidentreviewallowed":
      return (
        (thisUser.ownerOrganization || {})?.incidentreviewallowed?.toString() || "0"
      );
    
    case "incabcoachingallowed":
        return (
        (thisUser.ownerOrganization || {})?.incabcoachingallowed?.toString() || "0" //changelater
      );

    case "serializedziptieallowed":
      return (
        (thisUser.ownerOrganization || {})?.serializedziptieallowed?.toString() || "0"
      );

    case "userloginasallowed":
      return (
        (thisUser.ownerOrganization || {})?.userloginasallowed?.toString() || "0"
      );

    case "hasfleettags":
      return (
        (thisUser.ownerOrganization || {})?.hasfleettags?.toString() || "0"
      );

    case "haspanicsos":
      return (thisUser.ownerOrganization || {})?.haspanicsos?.toString() || "0";

    case "elogenabled":
      return (thisUser.ownerOrganization || {})?.elogenabled?.toString() || "0";

    case "isbordercrossing":
      return (
        (thisUser.ownerOrganization || {})?.isbordercrossing?.toString() || "0"
      );

    case "crashtrackEnabled":
      return (
        `${((thisUser.ownerOrganization || {}) as LegacyOrganizationAccount)?.enablecrashtrack}` ||
        "0"
      );

    case "geoCategoriesEnabled":
      return (
        `${((thisUser.ownerOrganization || {}) as LegacyOrganizationAccount)?.usegeocategories}` ||
        "0"
      );

    case "mapDefault":
      return (
        `${((thisUser.user || {}) as TLegacyUserWithAccount)?.mapDefault}` ||
        "0"
      );

    case "mapIconSize":
      return (
        `${((thisUser.ownerOrganization || {}) as LegacyOrganizationAccount)?.map_icon_size}` ||
        ACCOUNT_MAP_ICON_SIZE_DEFAULT_VALUE.toString()
      );

    default:
      return null;
  }
};

export const getAccountMapIconSizeNoHook = () => {
  const ownerOrganizationRaw = window.localStorage.getItem(
    localStorageKeys.ownerOrganization
  );
  const ownerOrganization = ownerOrganizationRaw
    ? (JSON.parse(ownerOrganizationRaw) as LegacyOrganizationAccount)
    : undefined;

  return (
    `${((ownerOrganization || {}) as LegacyOrganizationAccount)?.map_icon_size}` ||
    ACCOUNT_MAP_ICON_SIZE_DEFAULT_VALUE.toString()
  );
};

export const getAccountMapIconSizeMultiplierNoHook = () => {
  const mapIconSize = getAccountMapIconSizeNoHook();

  switch (parseInt(mapIconSize)) {
    case ACCOUNT_MAP_ICON_SIZE_VALUES.SMALL:
      return 1.0;
    case ACCOUNT_MAP_ICON_SIZE_VALUES.LARGE:
      return 1.5;
    case ACCOUNT_MAP_ICON_SIZE_VALUES.MEDIUM:
    default:
      return 1.2;
  }
};

export const userDisplayName = (user: TLegacyUserWithAccount) => {
  return (
    [user.displayname, user.contactname, user.userid].find((name) => !!name) ||
    ""
  );
};

export const userRoleDisplayName = (role: TLegacyUserRole) => {
  return !!role.displayname ? role.displayname : role.roleid;
};

export const userIsSysAdmin = (user: TLegacyUserWithAccount) => {
  return APP_CONFIG.SYSADMIN_SUPERADMIN_ACCOUNTS.includes(user?.accountid); // old condition user.roleid === '!sysadmin';
};

// to check if logged in user is a super admin
export const useLoggedInUserPerms = () => {
  const { getLocalStorageItem } = useLocalStorage();
  const thisUser = {
    user: getLocalStorageItem(localStorageKeys.user) as TLegacyUserWithAccount,
  };
  const { getUserACLAccessLevel } = useACL();
  const loggedInUserIsSysAdmin = () =>
    userIsSysAdmin(thisUser.user) &&
    getUserACLAccessLevel(ACL_NAMES.ACL_SYSADMIN_ACCOUNTS) >=
      ROLE_ACCESS_LEVELS.READ;

  //  To check mananger
  const loggedInUserIsManager = () => {
    if (!thisUser?.user) return false;
    const isParentAccount = thisUser.user.account?.is_parent_account === true;
    const hasManagerAcl =
      getUserACLAccessLevel(ACL_NAMES.ACL_ADMIN_MANAGER_LOOKUP) >=
      ROLE_ACCESS_LEVELS.READ;

    return isParentAccount && hasManagerAcl;
  };

  const getLoggedInUserDashboardUrl = (isSysAdmin?: boolean) => {
    return isSysAdmin || loggedInUserIsSysAdmin()
      ? routeUrls.sysadmin
      : routeUrls.dashboard;
  };

  return {
    getLoggedInUserDashboardUrl,
    loggedInUserIsSysAdmin,
    loggedInUserIsManager,
  };
};

// deprecated
export const userRoleIsPrivileged = (roleid: string) => {
  return roleid.charAt(0) === "!";
};
